import { useState, useEffect } from "react";
import axiosApi from "../Utils/axiosApi";
import { emptyRow } from "./constants";
import {
  generateBillText,
  printBill,
  sendWhatsAppBill,
} from "./helpers";

const useGuestManage = ({ singleBill = false } = {}) => {
  /* =====================================================
     FILTER
     ===================================================== */

  const [filter, setFilter] = useState({
    month: "",
    billDate: "",
    customerName: "",
    mobileNumber: "",
    paymentStatus: "",
    serviceType: "",
  });

  /* =====================================================
     ROWS
     ===================================================== */

  const [rows, setRows] = useState([]);

  /* =====================================================
     HOSTEL
     ===================================================== */

  const [hostelList, setHostelList] = useState([]);
  const [selectedHostel, setSelectedHostel] = useState("");

  /* =====================================================
     ROOM TYPES
     ===================================================== */

  const [roomTypes, setRoomTypes] = useState([]);

  /* =====================================================
     ROOM PRICES
     ===================================================== */

  const [roomPrices, setRoomPrices] = useState([]);
const [hostelRoomDTOs, setHostelRoomDTOs] = useState([]);
  /* =====================================================
     SEARCH
     ===================================================== */

  const [searchTriggered, setSearchTriggered] = useState(false);

  /* =====================================================
     DELETE
     ===================================================== */

  const [rowToDelete, setRowToDelete] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] =
    useState(false);

  /* =====================================================
     MESSAGES
     ===================================================== */

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  /* =====================================================
     MESS DETAILS
     ===================================================== */

  const [messDetailsRowIndex, setMessDetailsRowIndex] =
    useState(null);

  const [openMessDetailsDialog, setOpenMessDetailsDialog] =
    useState(false);

  /* =====================================================
     HOSTEL LIST
     ===================================================== */

  useEffect(() => {
    axiosApi
      .post("/hostel/getHostelName", {})
      .then((response) => {
        const list = Array.isArray(response.data)
          ? response.data
          : response.data?.hostels || [];

        setHostelList(
          list.map(([id, name]) => ({
            id,
            hostelName: name,
          }))
        );
      })
      .catch((error) => {
        console.error("Hostel list error:", error);
      });
  }, []);

  /* =====================================================
     ROOM TYPES + ROOM PRICES

     Hostel change:
       - API call for room types
       - API call for pricing

     Room type change:
       - NO API CALL
       - find price locally
     ===================================================== */

  useEffect(() => {
    if (!selectedHostel) {
      setRoomTypes([]);
      setRoomPrices([]);
      return;
    }

    const hostel = hostelList.find(
      (h) => h.hostelName === selectedHostel
    );

    if (!hostel?.id) {
      setRoomTypes([]);
      setRoomPrices([]);
      return;
    }

    /* =================================================
       ROOM TYPES
       ================================================= */

    axiosApi
      .post("/hostel/getHostelRoomType", {
        hostelSearchCriteria: {
          hostelIds: [hostel.id],
        },
      })
      .then((res) => {
        const data = Array.isArray(res.data)
          ? res.data
          : res.data?.roomTypes || [];

        console.log("ROOM TYPES:", data);

        setRoomTypes(data);
      })
      .catch((error) => {
        console.error("Room type error:", error);
        setRoomTypes([]);
      });

    /* =================================================
       ROOM PRICES
       ================================================= */

    axiosApi
      .post("/public/getHostelInfo", {
        hostelSearchCriteria: {
          hostelIds: [hostel.id],
          searchCheckInDate: null,
          searchCheckOutDate: null,
        },
      })
      .then((res) => {
  const dto = res.data;

  console.log("HOSTEL INFO:", dto);

  // Save the original room DTOs for the Room No. dropdown.
  const roomDTOs = Array.isArray(dto?.hostelRoomDTOs)
    ? dto.hostelRoomDTOs
    : [];

  setHostelRoomDTOs(roomDTOs);

  // Keep the existing room pricing functionality.
  const prices = roomDTOs.map((room) => ({
    ...room,
    roomType: room.roomType || "",
    dailyPrice: room.dailyPrice ?? 0,
    monthlyPrice: room.monthlyPrice ?? 0,
    availabilityCount: room.availabilityCount ?? 0,
    price2To3Hours: room.price2To3Hours ?? "",
    price4To6Hours: room.price4To6Hours ?? "",
    price6To12Hours: room.price6To12Hours ?? "",
    propertyType:
      room.propertyType ||
      room.property ||
      room.hostelPropertyType ||
      "",
  }));

  console.log("ROOM DTOs FOR DROPDOWN:", roomDTOs);
  console.log("ROOM PRICES:", prices);

  setRoomPrices(prices);
})
      .catch((error) => {
        console.error("Room price error:", error);
        setRoomPrices([]);
      });
  }, [selectedHostel, hostelList]);

  /* =====================================================
     DATE TIME HELPERS
     ===================================================== */

  const parseDateTime = (value) => {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime())
        ? null
        : value;
    }

    const stringValue = String(value).trim();

    if (!stringValue) {
      return null;
    }

    let date;

    /*
     * datetime-local:
     * 2026-10-03T10:30
     */
    if (
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(
        stringValue
      )
    ) {
      date = new Date(
        `${stringValue}:00`
      );
    } else {
      date = new Date(stringValue);
    }

    return Number.isNaN(date.getTime())
      ? null
      : date;
  };

  /* =====================================================
     FORMAT BACKEND DATETIME FOR DATETIME-LOCAL
     ===================================================== */

  const toDateTimeLocal = (value) => {
    if (!value) {
      return "";
    }

    const stringValue = String(value).trim();

    /*
     * Already:
     * 2026-10-03T10:30
     */
    if (
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(
        stringValue
      )
    ) {
      return stringValue;
    }

    /*
     * Backend:
     * 2026-10-03T10:30:00
     *
     * Keep the first 16 characters.
     */
    if (stringValue.includes("T")) {
      return stringValue.substring(0, 16);
    }

    /*
     * If backend returns a Date/ISO value
     */
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const pad = (number) =>
      String(number).padStart(2, "0");

    return `${date.getFullYear()}-${pad(
      date.getMonth() + 1
    )}-${pad(date.getDate())}T${pad(
      date.getHours()
    )}:${pad(date.getMinutes())}`;
  };

  /* =====================================================
     GET STAY DURATION MINUTES
     ===================================================== */

  const getStayDurationMinutes = (
    checkIn,
    checkOut
  ) => {
    const start = parseDateTime(checkIn);
    const end = parseDateTime(checkOut);

    if (!start || !end) {
      return null;
    }

    const difference =
      end.getTime() - start.getTime();

    if (difference < 0) {
      return null;
    }

    return Math.floor(
      difference / (1000 * 60)
    );
  };

  /* =====================================================
     CALENDAR DAYS

     Normal stay uses calendar days.

     Example:

     Oct 1 10:00
     Oct 3 09:00

     = 3 days
     ===================================================== */

  const calculateCalendarStayDays = (
    checkIn,
    checkOut
  ) => {
    if (!checkIn || !checkOut) {
      return 1;
    }

    const start = parseDateTime(checkIn);
    const end = parseDateTime(checkOut);

    if (!start || !end) {
      return 1;
    }

    const startDate = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate()
    );

    const endDate = new Date(
      end.getFullYear(),
      end.getMonth(),
      end.getDate()
    );

    if (endDate < startDate) {
      return 0;
    }

    return (
      Math.floor(
        (endDate.getTime() -
          startDate.getTime()) /
          (1000 * 60 * 60 * 24)
      ) + 1
    );
  };

  /* =====================================================
     PROPERTY TYPE
     ===================================================== */

  const getPropertyType = (
    row,
    roomPrice
  ) => {
    return String(
      row.propertyType ||
        row.property ||
        row.hostelPropertyType ||
        roomPrice?.propertyType ||
        ""
    )
      .trim()
      .toLowerCase();
  };

  /* =====================================================
     MICRO STAY
     ===================================================== */

  const isMicroStay = (
    row,
    roomPrice
  ) => {
    const propertyType =
      getPropertyType(row, roomPrice);

    return (
      propertyType === "micro stay" ||
      propertyType === "microstay" ||
      propertyType === "micro-stay"
    );
  };

  /* =====================================================
     MICRO STAY PRICE
     ===================================================== */

  const calculateMicroStayPrice = (
    roomPrice,
    durationMinutes
  ) => {
    if (
      !roomPrice ||
      durationMinutes === null ||
      durationMinutes === undefined
    ) {
      return {
        amount: 0,
        category: "",
      };
    }

    if (durationMinutes <= 0) {
      return {
        amount: 0,
        category: "",
      };
    }

    /*
     * 2 - 3 HOURS
     */

    if (
      durationMinutes >= 120 &&
      durationMinutes <= 180
    ) {
      return {
        amount: Number(
          roomPrice.price2To3Hours || 0
        ),
        category: "2-3 Hours",
      };
    }

    /*
     * MORE THAN 3 - 6 HOURS
     */

    if (
      durationMinutes > 180 &&
      durationMinutes <= 360
    ) {
      return {
        amount: Number(
          roomPrice.price4To6Hours || 0
        ),
        category: "4-6 Hours",
      };
    }

    /*
     * MORE THAN 6 - 12 HOURS
     */

    if (
      durationMinutes > 360 &&
      durationMinutes <= 720
    ) {
      return {
        amount: Number(
          roomPrice.price6To12Hours || 0
        ),
        category: "6-12 Hours",
      };
    }

    /*
     * BELOW 2 HOURS
     */

    if (durationMinutes < 120) {
      return {
        amount: 0,
        category: "Below 2 Hours",
      };
    }

    /*
     * MORE THAN 12 HOURS
     */

    return {
      amount: 0,
      category: "More than 12 Hours",
    };
  };

  /* =====================================================
     CALCULATE STAY DETAILS
     ===================================================== */

  const calculateStayDetails = (
    row,
    roomPrice
  ) => {
    if (!roomPrice) {
      return {
        roomRent: 0,
        stayRate: 0,
        stayDays: Number(row.stayDays || 1),
        stayDurationMinutes: null,
        stayDurationHours: null,
        microStayCategory: "",
      };
    }

    const roomRent = Number(
      roomPrice.dailyPrice || 0
    );

    /* =================================================
       MICRO STAY
       ================================================= */

    if (isMicroStay(row, roomPrice)) {
      const durationMinutes =
        getStayDurationMinutes(
          row.checkIn,
          row.checkOut
        );

      if (durationMinutes === null) {
        return {
          roomRent,

          stayRate: 0,

          stayDays: 1,

          stayDurationMinutes: null,

          stayDurationHours: null,

          microStayCategory: "",
        };
      }

      const microPrice =
        calculateMicroStayPrice(
          roomPrice,
          durationMinutes
        );

      return {
        /*
         * Micro Stay does not use
         * daily stay calculation.
         */
        stayDays: 1,

        /*
         * Display daily room rent.
         */
        roomRent,

        /*
         * Actual micro stay slab rent.
         */
        stayRate:
          microPrice.amount,

        stayDurationMinutes:
          durationMinutes,

        stayDurationHours:
          Number(
            (
              durationMinutes / 60
            ).toFixed(2)
          ),

        microStayCategory:
          microPrice.category,
      };
    }

    /* =================================================
       NORMAL STAY
       ================================================= */

    const stayDays =
      calculateCalendarStayDays(
        row.checkIn,
        row.checkOut
      );

    return {
      stayDays,

      roomRent,

      /*
       * TOTAL stay rent.
       */
      stayRate:
        roomRent *
        Math.max(stayDays, 0),

      stayDurationMinutes: null,

      stayDurationHours: null,

      microStayCategory: "",
    };
  };

  /* =====================================================
     FIND ROOM PRICE
     ===================================================== */

  const findRoomPrice = (
    roomType
  ) => {
    if (!roomType) {
      return null;
    }

    return (
      roomPrices.find(
        (room) =>
          String(room.roomType || "")
            .trim()
            .toLowerCase() ===
          String(roomType || "")
            .trim()
            .toLowerCase()
      ) || null
    );
  };

  /* =====================================================
     HANDLE CHANGE
     ===================================================== */

  const handleChange = (
    index,
    field,
    value
  ) => {
    setRows((prevRows) => {
      const updated = [...prevRows];

      const row = {
        ...updated[index],
      };

      row[field] = value;
if (field === "finalBill") {
  row.finalBillManual = true;
}
      /* =================================================
         ROOM TYPE
         ================================================= */

      if (field === "roomType") {
        const selectedRoomPrice =
          findRoomPrice(value);

        console.log(
          "Selected Room Type:",
          value
        );

        console.log(
          "Matched Room Price:",
          selectedRoomPrice
        );

        /*
         * New room selection resets
         * manual Stay Rent.
         */

        row.stayRateManual = false;

        if (selectedRoomPrice) {
          row.propertyType =
            selectedRoomPrice.propertyType ||
            row.propertyType ||
            "";

          const stayDetails =
            calculateStayDetails(
              row,
              selectedRoomPrice
            );

          row.roomRent =
            stayDetails.roomRent;

          row.stayDays =
            stayDetails.stayDays;

          row.stayRate =
            stayDetails.stayRate;

          row.stayDurationMinutes =
            stayDetails.stayDurationMinutes;

          row.stayDurationHours =
            stayDetails.stayDurationHours;

          row.microStayCategory =
            stayDetails.microStayCategory;
        } else {
          row.roomRent = 0;

          row.stayRate = 0;

          row.stayDurationMinutes =
            null;

          row.stayDurationHours =
            null;

          row.microStayCategory = "";
        }
      }

      /* =================================================
         MANUAL STAY RENT
         ================================================= */

      if (field === "stayRate") {
        row.stayRateManual = true;
      }

      /* =================================================
         CHECK-IN / CHECK-OUT DATETIME
         ================================================= */

      if (
        field === "checkIn" ||
        field === "checkOut"
      ) {
        row.isManualDays = false;

        const roomPrice =
          findRoomPrice(row.roomType);

        /*
         * Do not overwrite manually
         * entered Stay Rent.
         */

        if (
          roomPrice &&
          !row.stayRateManual
        ) {
          const stayDetails =
            calculateStayDetails(
              row,
              roomPrice
            );

          row.roomRent =
            stayDetails.roomRent;

          row.stayDays =
            stayDetails.stayDays;

          row.stayRate =
            stayDetails.stayRate;

          row.stayDurationMinutes =
            stayDetails.stayDurationMinutes;

          row.stayDurationHours =
            stayDetails.stayDurationHours;

          row.microStayCategory =
            stayDetails.microStayCategory;
        }
      }

      /* =================================================
         MANUAL STAY DAYS
         ================================================= */

      if (field === "stayDays") {
        row.isManualDays = true;

        const roomPrice =
          findRoomPrice(row.roomType);

        /*
         * Micro Stay does not use
         * manual days.
         */

        if (
          !isMicroStay(
            row,
            roomPrice
          ) &&
          !row.stayRateManual
        ) {
          const roomRent =
            Number(row.roomRent || 0);

          const stayDays =
            Number(value || 1);

          row.stayRate =
            roomRent *
            stayDays;
        }
      }

      /* =================================================
         SERVICE TYPE
         ================================================= */

      if (field === "serviceType") {
        if (value === "mess") {
          row.stayRate = "";
          row.roomRent = "";
          row.roomType = "";
          row.roomNumber = "";
          row.propertyType = "";

          row.stayRateManual = false;

          row.stayDurationMinutes =
            null;

          row.stayDurationHours =
            null;

          row.microStayCategory = "";
        }

        if (value === "stay") {
          row.rate = "";
          row.count = 1;
          row.messTotal = 0;
        }

        if (value === "both") {
          const roomPrice =
            findRoomPrice(row.roomType);

          if (
            roomPrice &&
            !row.stayRateManual
          ) {
            const stayDetails =
              calculateStayDetails(
                row,
                roomPrice
              );

            row.roomRent =
              stayDetails.roomRent;

            row.stayDays =
              stayDetails.stayDays;

            row.stayRate =
              stayDetails.stayRate;

            row.stayDurationMinutes =
              stayDetails.stayDurationMinutes;

            row.stayDurationHours =
              stayDetails.stayDurationHours;

            row.microStayCategory =
              stayDetails.microStayCategory;
          }
        }
      }

      /* =================================================
         MESS DATE CALCULATION
         ================================================= */

      if (
        row.serviceType === "mess" &&
        row.checkIn &&
        row.checkOut
      ) {
        const messDays =
          calculateCalendarStayDays(
            row.checkIn,
            row.checkOut
          );

        if (messDays > 0) {
          row.stayDays = messDays;
        }
      }

      /* =================================================
         NUMERIC VALUES
         ================================================= */

      const stayDays =
        Number(row.stayDays || 1);

      const count =
        Number(row.count || 1);

      const messRate =
        Number(row.rate || 0);

      /*
       * IMPORTANT:
       * stayRate is TOTAL stay rent.
       */

      const stayRate =
        Number(row.stayRate || 0);

      const discount =
        Number(row.discount || 0);

      const received =
        Number(row.received || 0);

      /* =================================================
         TOTALS
         ================================================= */

      let messTotal = 0;
      let stayTotal = 0;

      if (row.serviceType === "mess") {
        messTotal =
          stayDays *
          count *
          messRate;
      }

      if (row.serviceType === "stay") {
        stayTotal = stayRate;
      }

      if (row.serviceType === "both") {
        messTotal =
          stayDays *
          count *
          messRate;

        stayTotal = stayRate;
      }

      row.messTotal = messTotal;
      row.stayTotal = stayTotal;

      /* =================================================
         TOTAL
         ================================================= */

      const calculatedTotal =
        Math.max(
          messTotal +
            stayTotal -
            discount,
          0
        );

      row.total = calculatedTotal;

    

      /* =================================================
   FINAL BILL
   ================================================= */

/*
 * Initially:
 * Final Bill = Calculated Total
 *
 * If user manually edits Final Bill:
 * keep the edited amount.
 */

if (!row.finalBillManual) {
  row.finalBill = calculatedTotal;
}

const finalBill = Number(row.finalBill || 0);

      /* =================================================
         BALANCE
         ================================================= */

      row.balance =
        finalBill -
        received;

      /* =================================================
         PAYMENT STATUS
         ================================================= */

      if (
        row.balance <= 0 &&
        finalBill > 0
      ) {
        row.paymentStatus = "paid";
      } else if (received > 0) {
        row.paymentStatus = "partial";
      } else {
        row.paymentStatus = "pending";
      }

      row.errorMsg = "";
      row.successMsg = "";

      updated[index] = row;

      return updated;
    });
  };

  /* =====================================================
     EDIT ROW
     ===================================================== */

  const handleEditRow = (
    index
  ) => {
    setRows((prevRows) => {
      const updated = [...prevRows];

      updated[index] = {
        ...updated[index],

        isEditing: true,

        errorMsg: "",
        successMsg: "",
      };

      return updated;
    });
  };

  /* =====================================================
     OPEN MESS DETAILS
     ===================================================== */

  const handleOpenMessDetails = (
    index
  ) => {
    const row = rows[index];

    if (!row) {
      return;
    }

    if (
      row.isSaved &&
      !row.isEditing
    ) {
      return;
    }

    setMessDetailsRowIndex(index);

    setOpenMessDetailsDialog(true);
  };

  /* =====================================================
     CLOSE MESS DETAILS
     ===================================================== */

  const handleCloseMessDetails =
    () => {
      setOpenMessDetailsDialog(false);

      setMessDetailsRowIndex(null);
    };

  /* =====================================================
     ADD ROW
     ===================================================== */

  const addRow = () => {
    if (
      rows.some(
        (r) => !r.isSaved
      )
    ) {
      setErrorMsg(
        "Please save current row before adding new one"
      );

      return;
    }

    setRows([
      ...rows,
      {
        ...emptyRow,

        isSaved: false,
        isEditing: true,
finalBill: 0,
  finalBillManual: false,

  

  
        dailyMessDetails: [],

        stayRateManual: false,

        propertyType: "",

        stayDurationMinutes: null,

        stayDurationHours: null,

        microStayCategory: "",

        /*
         * Single datetime fields.
         */
        checkIn: "",
        checkOut: "",

        errorMsg: "",
        successMsg: "",
      },
    ]);
  };

  
/* =====================================================
   SAVE ROW — MONTHLY BILL + SINGLE BILL
   ===================================================== */

const handleSaveRow = async (index) => {
  const row = rows[index];

  if (!row) {
    return;
  }

  const setRowError = (message) => {
    setRows((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              errorMsg: message,
              successMsg: "",
            }
          : item
      )
    );
  };

  /* =====================================================
     BILL MONTH
     Monthly page requires a month.
     Single Bill derives the month from Check-In.
     ===================================================== */

  if (!singleBill && !filter.month) {
    setRowError("Bill month required");
    return;
  }

  /* =====================================================
     ENTRY DATE
     ===================================================== */

  if (!row.date) {
    setRowError("Entry date required");
    return;
  }

  /* =====================================================
     CHECK-IN DATE + MONTH VALIDATION
     ===================================================== */

  if (!row.checkIn) {
    setRowError("Check-In date required");
    return;
  }

  const checkInDate = parseDateTime(row.checkIn);

  if (!checkInDate) {
    setRowError("Invalid Check-In date");
    return;
  }

  const checkInMonth =
    `${checkInDate.getFullYear()}-${String(
      checkInDate.getMonth() + 1
    ).padStart(2, "0")}`;

  if (!singleBill) {
    const selectedFilterMonth = filter.month.substring(0, 7);

    if (checkInMonth !== selectedFilterMonth) {
      setRowError(
        "Check-In month must match the selected filter month"
      );
      return;
    }
  }

  /* =====================================================
     CUSTOMER VALIDATION — BOTH PAGES
     ===================================================== */

  if (!row.name?.trim()) {
    setRowError("Customer name required");
    return;
  }

  if (!row.customerMobileNumber?.trim()) {
    setRowError("Customer Mobile Number required");
    return;
  }

  /* =====================================================
     COUNT
     ===================================================== */

  if (Number(row.count || 0) <= 0) {
    setRowError("Count must be at least 1");
    return;
  }

  /* =====================================================
     RECEIVED AMOUNT
     ===================================================== */

  if (Number(row.received || 0) < 0) {
    setRowError("Received amount cannot be negative");
    return;
  }

  /* =====================================================
     STAY VALIDATION
     ===================================================== */

  if (
    row.serviceType === "stay" ||
    row.serviceType === "both"
  ) {
    if (!row.checkIn || !row.checkOut) {
      setRowError(
        "Check-In & Check-Out date/time required"
      );
      return;
    }

    const stayCheckIn = parseDateTime(row.checkIn);
    const stayCheckOut = parseDateTime(row.checkOut);

    if (!stayCheckIn || !stayCheckOut) {
      setRowError(
        "Invalid Check-In or Check-Out date/time"
      );
      return;
    }

    if (stayCheckOut < stayCheckIn) {
      setRowError(
        "Check-Out cannot be before Check-In"
      );
      return;
    }

    /* ROOM PRICE */

    const roomPrice = findRoomPrice(row.roomType);

    const microStay = isMicroStay(row, roomPrice);

    /* MICRO STAY VALIDATION */

    if (microStay) {
      const durationMinutes = getStayDurationMinutes(
        row.checkIn,
        row.checkOut
      );

      if (durationMinutes === null) {
        setRowError("Invalid Micro Stay duration");
        return;
      }

      if (durationMinutes < 120) {
        setRowError(
          "Micro Stay minimum duration is 2 hours"
        );
        return;
      }

      if (durationMinutes > 720) {
        setRowError(
          "Micro Stay maximum duration is 12 hours"
        );
        return;
      }

      row.stayDurationMinutes = durationMinutes;

      row.stayDurationHours = Number(
        (durationMinutes / 60).toFixed(2)
      );

      if (!row.microStayCategory) {
        row.microStayCategory =
          calculateMicroStayPrice(
            roomPrice,
            durationMinutes
          ).category;
      }

      if (!row.stayRateManual) {
        const stayDetails = calculateStayDetails(
          row,
          roomPrice
        );

        if (
          !stayDetails.stayRate ||
          stayDetails.stayRate <= 0
        ) {
          setRowError(
            `No Micro Stay price configured for ${
              stayDetails.microStayCategory ||
              "this duration"
            }`
          );
          return;
        }

        row.stayRate = stayDetails.stayRate;
        row.roomRent = stayDetails.roomRent;
        row.stayDays = stayDetails.stayDays;
        row.stayDurationMinutes =
          stayDetails.stayDurationMinutes;
        row.stayDurationHours =
          stayDetails.stayDurationHours;
        row.microStayCategory =
          stayDetails.microStayCategory;
      }
    }

    /* STAY RENT */

    if (
      row.stayRate === "" ||
      row.stayRate === null ||
      row.stayRate === undefined ||
      !Number.isFinite(Number(row.stayRate)) ||
      Number(row.stayRate) <= 0
    ) {
      setRowError("Stay rent required");
      return;
    }

    /* ROOM TYPE */

    if (!row.roomType) {
      setRowError("Room type required");
      return;
    }
  }

  /* =====================================================
     MESS VALIDATION
     ===================================================== */

  if (
    row.serviceType === "mess" ||
    row.serviceType === "both"
  ) {
    if (
      row.rate === "" ||
      row.rate === null ||
      row.rate === undefined ||
      !Number.isFinite(Number(row.rate)) ||
      Number(row.rate) <= 0
    ) {
      setRowError("Mess charge required");
      return;
    }

    if (!row.checkIn || !row.checkOut) {
      setRowError("From Date & To Date required");
      return;
    }

    const messCheckIn = parseDateTime(row.checkIn);
    const messCheckOut = parseDateTime(row.checkOut);

    if (!messCheckIn || !messCheckOut) {
      setRowError("Invalid From Date or To Date");
      return;
    }

    if (messCheckOut < messCheckIn) {
      setRowError("To Date cannot be before From Date");
      return;
    }
  }

  /* =====================================================
     HOSTEL VALIDATION
     ===================================================== */

  const hostel = hostelList.find(
    (h) =>
      String(h.id) === String(selectedHostel) ||
      h.hostelName === selectedHostel
  );

  if (!hostel?.id) {
    setRowError("Invalid hostel selected");
    return;
  }

  try {
    /* CLEAR ERROR */

    setRows((prev) =>
      prev.map((item, i) =>
        i === index
          ? { ...item, errorMsg: "", successMsg: "" }
          : item
      )
    );

    /* =====================================================
       NUMERIC VALUES
       ===================================================== */

    const stayDays = Number(row.stayDays || 1);
    const count = Number(row.count || 1);
    const messRate = Number(row.rate || 0);
    const stayRate = Number(row.stayRate || 0);
    const discount = Number(row.discount || 0);
    const received = Number(row.received || 0);

    /* =====================================================
       TOTALS
       ===================================================== */

    let messTotal = 0;
    let stayTotal = 0;

    if (
      row.serviceType === "mess" ||
      row.serviceType === "both"
    ) {
      messTotal = stayDays * count * messRate;
    }

    if (
      row.serviceType === "stay" ||
      row.serviceType === "both"
    ) {
      // Stay Rate is already the total stay rent.
      stayTotal = stayRate;
    }

    const calculatedTotal = Math.max(
      messTotal + stayTotal - discount,
      0
    );

    const finalBill =
      row.finalBill !== undefined &&
      row.finalBill !== null &&
      row.finalBill !== ""
        ? Number(row.finalBill)
        : calculatedTotal;

    if (!Number.isFinite(finalBill) || finalBill < 0) {
      setRowError("Final bill must be a valid non-negative amount");
      return;
    }

    /* =====================================================
       PAYLOAD
       ===================================================== */

    const payload = {
      hostelId: hostel.id,

      stayServiceBookingDTO: {
        id: row.id || null,

        hostelId: hostel.id,

        // Derived from validated Check-In.
        billMonth: checkInMonth,

        entryDate: row.date || null,

        checkIn: row.checkIn || null,

        checkOut: row.checkOut || null,

        propertyType: row.propertyType || "",

        serviceType: row.serviceType,

        // Total stay rent.
        stayRate,

        // Daily room rent.
        roomRent: Number(row.roomRent || 0),

        messRate,

        customerName: row.name.trim(),

        customerMobileNumber:
          row.customerMobileNumber.trim(),

        address: row.address || "",

        count,

        stayDays,

        discount,

        // Preserves the manually entered final bill.
        totalAmount: finalBill,

        receivedAmount: received,

        roomType: row.roomType || "",

        roomNumber: row.roomNumber || "",

        remarks: row.remarks || "",

        stayDurationMinutes:
          row.stayDurationMinutes ?? null,

        stayDurationHours:
          row.stayDurationHours ?? null,

        microStayCategory:
          row.microStayCategory || "",

        dailyMealRecordDTOs:
          row.dailyMessDetails || [],

        guestDetailDTOs: (row.guestDetails || []).map(
          (guest) => ({
            guestName: guest.guestName,
            age: Number(guest.age),
            phoneNumber: guest.phoneNumber,
          })
        ),
      },
    };

    console.log("SAVE PAYLOAD:", payload);

    /* =====================================================
       SAVE API
       ===================================================== */

    await axiosApi.post(
      "/stayServiceBooking/updateStayServiceBooking",
      payload
    );

    /* =====================================================
       SUCCESS
       ===================================================== */

    setRows((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,

              isSaved: true,
              isEditing: false,

              finalBill,
              total: calculatedTotal,

              messTotal,
              stayTotal,

              balance: finalBill - received,

              paymentStatus:
                finalBill - received <= 0 && finalBill > 0
                  ? "paid"
                  : received > 0
                  ? "partial"
                  : "pending",

              successMsg: "Entry saved successfully",
              errorMsg: "",
            }
          : item
      )
    );
	return true;
  } catch (err) {
    console.error("Save error:", err);

    const message =
      err?.response?.data?.message ||
      "Save failed. Please try again.";

    setRows((prev) =>
      prev.map((item, i) =>
        i === index
          ? {
              ...item,
              errorMsg: message,
              successMsg: "",
            }
          : item
      )
    );
	return false;
  }
};


  /* =====================================================
     DELETE
     ===================================================== */

  const confirmDelete = async () => {
    if (!rowToDelete) {
      return;
    }

    const rowIndex =
      rows.findIndex(
        (r) =>
          r === rowToDelete ||
          r.id === rowToDelete.id
      );

    try {
      if (
        rowToDelete.id !== undefined &&
        rowToDelete.id !== null
      ) {
        await axiosApi.post(
          "/stayServiceBooking/deleteStayServiceBooking",
          {
            stayServiceBookingIds: [
              Number(rowToDelete.id),
            ],
          }
        );
      }

      setRows((prev) => {
        const updated = [...prev];

        if (rowIndex !== -1) {
          updated[rowIndex].successMsg =
            "Deleted successfully";

          updated[rowIndex].errorMsg = "";
        }

        return updated;
      });

      setTimeout(() => {
        setRows((prev) =>
          rowToDelete.id
            ? prev.filter(
                (r) =>
                  r.id !==
                  rowToDelete.id
              )
            : prev.filter(
                (r) =>
                  r !== rowToDelete
              )
        );
      }, 600);
    } catch (err) {
      console.error(
        "Delete error:",
        err
      );

      if (rowIndex !== -1) {
        setRows((prev) => {
          const updated = [...prev];

          updated[rowIndex].errorMsg =
            "Delete failed";

          updated[rowIndex].successMsg = "";

          return updated;
        });
      }
    } finally {
      setOpenDeleteDialog(false);
      setRowToDelete(null);
    }
  };

  /* =====================================================
     SEARCH
     ===================================================== */

  const handleSearch = async () => {
    if (
      !selectedHostel ||
      !filter.month
    ) {
      setErrorMsg(
        "Please select Hostel and Month"
      );

      return;
    }

    try {
      setErrorMsg("");

      const hostel =
        hostelList.find(
          (h) =>
            h.hostelName ===
            selectedHostel
        );

      const response =
        await axiosApi.post(
          "/stayServiceBooking/getStayServiceBooking",
          {
            hostelId:
              hostel?.id || null,

            billMonth:
              filter.month || null,

            billDate:
              filter.billDate || null,

            customerName:
              filter.customerName || null,

            customerMobileNumber:
              filter.mobileNumber || null,

            paymentStatus:
              filter.paymentStatus || null,

            serviceType:
              filter.serviceType || null,
          }
        );

      const data =
        response.data || [];

      /* =================================================
         MAP API DATA
         ================================================= */

      const mappedRows =
        data.map((item) => {
          const serviceType =
            item.serviceType?.toLowerCase() ||
            "mess";

          const stayDays =
            Number(item.stayDays || 1);

          const count =
            Number(item.count || 1);

          const messRate =
            Number(item.messRate || 0);

          /*
           * Saved Stay Rent is total.
           */

          const stayRate =
            Number(item.stayRate || 0);

          const discount =
            Number(item.discount || 0);

          /* =================================================
             ROOM TYPE
             ================================================= */

          const roomType =
            (item.roomType || "").trim();

          /* =================================================
             ROOM PRICE
             ================================================= */

          const matchedRoomPrice =
            findRoomPrice(roomType);

          /* =================================================
             PROPERTY TYPE
             ================================================= */

          const propertyType =
            item.propertyType ||
            item.property ||
            item.hostelPropertyType ||
            matchedRoomPrice?.propertyType ||
            "";

          /* =================================================
             MESS TOTAL
             ================================================= */

          let messTotal = 0;

          if (
            serviceType === "mess" ||
            serviceType === "both"
          ) {
            messTotal =
              stayDays *
              count *
              messRate;
          }

          /* =================================================
             STAY TOTAL
             ================================================= */

          let stayTotal = 0;

          if (
            serviceType === "stay" ||
            serviceType === "both"
          ) {
            stayTotal = stayRate;
          }

          /* =================================================
             TOTAL
             ================================================= */

          const calculatedTotal =
            Math.max(
              messTotal +
                stayTotal -
                discount,
              0
            );

          /* =================================================
             FINAL BILL
             ================================================= */

          const savedFinalBill =
            Number(
              item.totalAmount ??
                calculatedTotal
            );

          /* =================================================
             RECEIVED
             ================================================= */

          const received =
            Number(
              item.receivedAmount || 0
            );

          /* =================================================
             BALANCE
             ================================================= */

          const balance =
            savedFinalBill -
            received;

          /* =================================================
             PAYMENT STATUS
             ================================================= */

          let paymentStatus =
            "pending";

          if (
            balance <= 0 &&
            savedFinalBill > 0
          ) {
            paymentStatus = "paid";
          } else if (
            received > 0
          ) {
            paymentStatus = "partial";
          }

          /* =================================================
             ROOM RENT
             ================================================= */

          const roomRent =
            item.roomRent ??
            matchedRoomPrice?.dailyPrice ??
            "";

          /* =================================================
             CHECK-IN / CHECK-OUT
             ================================================= */

          const checkIn =
            toDateTimeLocal(
              item.checkIn
            );

          const checkOut =
            toDateTimeLocal(
              item.checkOut
            );

          /* =================================================
             MICRO STAY DURATION
             ================================================= */

          let stayDurationMinutes =
            item.stayDurationMinutes ??
            null;

          let stayDurationHours =
            item.stayDurationHours ??
            null;

          let microStayCategory =
            item.microStayCategory ||
            "";

          if (
            isMicroStay(
              { propertyType },
              matchedRoomPrice
            ) &&
            checkIn &&
            checkOut
          ) {
            const duration =
              getStayDurationMinutes(
                checkIn,
                checkOut
              );

            if (duration !== null) {
              stayDurationMinutes =
                duration;

              stayDurationHours =
                Number(
                  (
                    duration / 60
                  ).toFixed(2)
                );

              if (
                !microStayCategory
              ) {
                microStayCategory =
                  calculateMicroStayPrice(
                    matchedRoomPrice,
                    duration
                  ).category;
              }
            }
          }

          /* =================================================
             RETURN ROW
             ================================================= */

          return {
            id:
              item.stayServiceBookingId ||
              item.id ||
              item.shortStayId,

            date:
              item.entryDate?.split("T")[0] ||
              "",

            /*
             * SINGLE DATETIME FIELDS
             */

            checkIn,

            checkOut,

            propertyType,

            serviceType,

            /*
             * RATES
             */

            stayRate:
              item.stayRate ?? "",

            roomRent,

            rate:
              item.messRate ?? "",

            /*
             * MICRO STAY
             */

            stayDurationMinutes,

            stayDurationHours,

            microStayCategory,

            /*
             * CUSTOMER
             */

            name:
              item.customerName || "",

            customerMobileNumber:
              item.customerMobileNumber || "",

            address:
              item.address || "",

            /*
             * COUNTS
             */

            count:
              item.count || 1,

            stayDays:
              item.stayDays || 1,

            discount:
              item.discount || "",

            /*
             * PAYMENT
             */

            paymentStatus,

            /*
             * TOTALS
             */

            messTotal,

            stayTotal,

            total:
              calculatedTotal,

            finalBill:
              savedFinalBill,

            received:
              item.receivedAmount || "",

            balance,

            /*
             * OTHER
             */

            remarks:
              item.remarks || "",

            roomType,

            roomNumber:
              item.roomNumber || "",

            /*
             * DAILY MESS
             */

            dailyMessDetails:
              Array.isArray(
                item.dailyMessDetails
              )
                ? item.dailyMessDetails.map(
                    (detail) => ({
                      ...detail,

                      hostelId:
                        detail.hostelId ??
                        hostel?.id ??
                        item.hostelId ??
                        null,
                    })
                  )
                : [],

            /*
             * UI
             */

            isSaved: true,

            isEditing: false,

            /*
             * Existing saved Stay Rent
             * must remain unchanged when
             * datetime is edited.
             */

            stayRateManual: true,

            errorMsg: "",

            successMsg: "",
          };
        });

      console.log(
        "FETCHED / MAPPED ROWS:",
        mappedRows
      );

      setRows(mappedRows);

      setSearchTriggered(true);
    } catch (err) {
      console.error(
        "Search error:",
        err
      );

      setErrorMsg(
        "Failed to fetch data"
      );
    }
  };

  /* =====================================================
     TOTALS
     ===================================================== */

  const totalAmount = () =>
    rows.reduce(
      (sum, row) =>
        sum +
        Number(
          row.finalBill ??
            row.total ??
            0
        ),
      0
    );

  const totalReceived = () =>
    rows.reduce(
      (sum, row) =>
        sum +
        Number(row.received || 0),
      0
    );

  const totalBalance = () =>
    totalAmount() -
    totalReceived();

  /* =====================================================
     STATUS COLOR
     ===================================================== */

  const statusColor = (
    status
  ) => {
    if (
      status === "paid" ||
      status === "completed"
    ) {
      return "success";
    }

    if (
      status === "partial" ||
      status ===
        "partially_completed"
    ) {
      return "warning";
    }

    if (
      status === "pending" ||
      status === "not_started"
    ) {
      return "default";
    }

    return "default";
  };

  /* =====================================================
     STATUS LABEL
     ===================================================== */

  const statusLabel = (
    status
  ) => {
    if (status === "paid") {
      return "Completed";
    }

    if (status === "partial") {
      return "Partial";
    }

    return "Pending";
  };

  /* =====================================================
     PRINT
     ===================================================== */

  const handlePrint = (
    row
  ) => {
    const billText =
      generateBillText(
        row,
        selectedHostel,
        filter.month
      );

    printBill(billText);
  };

  /* =====================================================
     WHATSAPP
     ===================================================== */

  const handleWhatsApp = (
    row
  ) => {
    let phoneNumber =
      row.customerMobileNumber?.trim();

    if (!phoneNumber) {
      setErrorMsg(
        "Customer mobile number not available"
      );

      return;
    }

    phoneNumber =
      phoneNumber.replace(
        /\D/g,
        ""
      );

    if (
      phoneNumber.startsWith("91") &&
      phoneNumber.length === 12
    ) {
      phoneNumber =
        phoneNumber.slice(2);
    }

    if (
      !/^[6-9]\d{9}$/.test(
        phoneNumber
      )
    ) {
      setErrorMsg(
        "Invalid mobile number"
      );

      return;
    }

    const billText =
      generateBillText(
        row,
        selectedHostel,
        filter.month
      );

    sendWhatsAppBill(
      phoneNumber,
      billText
    );
  };

  /* =====================================================
     RETURN
     ===================================================== */

  return {
    filter,
    setFilter,

    rows,
    setRows,

    hostelList,

    selectedHostel,
    setSelectedHostel,

    roomTypes,

    roomPrices,
 hostelRoomDTOs, // ADD THIS
    searchTriggered,
    setSearchTriggered,

    handleSearch,

    handleChange,

    handleEditRow,

    addRow,

    handleSaveRow,

    confirmDelete,

    messDetailsRowIndex,

    openMessDetailsDialog,

    handleOpenMessDetails,

    handleCloseMessDetails,

    rowToDelete,
    setRowToDelete,

    openDeleteDialog,
    setOpenDeleteDialog,

    successMsg,
    setSuccessMsg,

    errorMsg,
    setErrorMsg,

    totalAmount,
    totalReceived,
    totalBalance,

    statusColor,
    statusLabel,

    handlePrint,
    handleWhatsApp,
  };
};

export default useGuestManage;